import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import styled from 'styled-components';
import { Menubar as PrimeReactMenuBar } from 'primereact/menubar';
import ConfirmDialog from 'components/UI/ConfirmDialog';
import Text from 'components/UI/Text';
import { useStores } from 'stores/hooks/hooks';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid/Grid';
import { MenuItem } from 'primereact/menuitem';
import { AREA_UNITS } from 'core/constants';
import { ActionMode, DialogCloseAction } from 'models/shared';
import CampaignCreateDialog from './CampaignCreateDialog/CampaignCreateDialog';
import CampaignUploadS3Dialog from './CampaignUploads3Dialog/CampaignUploads3Dialog';

const StyledInput = styled(Input)`
  background-color: ${(p) => p.theme.palette.background.default};
`;

const StyledPrimeReactMenuBar = styled(PrimeReactMenuBar)`
  .p-submenu-list {
    width: auto;
  }

  .p-menuitem-text {
    text-wrap: nowrap;
  }
`;

const SelectedNodeTitle = styled(Text)`
  color: ${({ theme }) => theme.color.text};
  font-weight: 600;
`;

const defaultUnitValue = AREA_UNITS[0].id;

interface MenuBarEndComponentsProps {
  handleSearchChange: (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
}

// const MenuBarEndComponents: FC<MenuBarEndComponentsProps> = ({
//   handleSearchChange,
// }) => {
//   const { t } = useTranslation('users');

//   return (
//     <Grid gap="1rem" direction="row">
//       {/* <StartSelect /> */}
//       <StyledInput
//         size="small"
//         placeholder={t('search')}
//         type="text"
//         onChange={handleSearchChange}
//       />
//     </Grid>
//   );
// };

const Menubar: FC<{ selectedTab: number }> = ({ selectedTab }) => {
  const { t } = useTranslation('campaigns');
  const { t: generalT } = useTranslation('general');
  const { campaignsStore } = useStores();

  const [menuModel, setMenuModel] = useState<MenuItem[]>([]);
  const [campaignActionMode, setCampaignActionMode] =
    useState<ActionMode>(ActionMode.CREATE);

  const [campaignManagementDialog, setCampaignManagementDialog] =
    useState(false);

  const [s3UploadDialog, setS3UploadDialog] = useState(false);

  const [deleteCampaignDialog, setDeleteCampaignDialog] = useState(false);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      // campaignsStore.setGlobalFilter((event.target.value || '').trim());
    },
    [],
  );

  const openCreateDialog = useCallback(() => {
    setCampaignActionMode(ActionMode.CREATE);
    setCampaignManagementDialog(true);
  }, []);

  const openEditDialog = useCallback(() => {
    setCampaignActionMode(ActionMode.EDIT);
    setCampaignManagementDialog(true);
  }, []);

  const closeManagementDialog = useCallback(() => {
    setCampaignManagementDialog(false);
  }, []);

  const openUploadS3Dialog = useCallback(() => {
    setS3UploadDialog(true);
  }, []);

  const closeUploadS3Dialog = useCallback(() => {
    setS3UploadDialog(false);
  }, []);

  const openDeleteDialog = useCallback(() => {
    setDeleteCampaignDialog(true);
  }, []);

  const onCloseRemoveDialog = useCallback(
    (response: DialogCloseAction) => {
      if (response === DialogCloseAction.Confirmed) {
        campaignsStore.deleteSelectedCampaign();
      }

      setDeleteCampaignDialog(false);
    },
    [campaignsStore],
  );

  const campaignItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('create_campaign'),
        icon: 'pi pi-fw pi-plus',
        command: () => {
          openCreateDialog();
        },
      },
      {
        label: t('edit_campaign'),
        disabled: !campaignsStore.selectedCampaign,
        icon: 'pi pi-fw pi-pencil',
        command: () => {
          openEditDialog();
        },
      },
      {
        label: t('upload_s3'),
        icon: 'pi pi-fw pi-upload',
        command: () => {
          openUploadS3Dialog();
        },
      },
      {
        label: t('delete_campaign'),
        disabled: !campaignsStore.selectedCampaign,
        icon: 'pi pi-fw pi-trash',
        command: () => {
          openDeleteDialog();
        },
      }
    ],
    [
      t,
      campaignsStore.selectedCampaign,
      openCreateDialog,
      openEditDialog,
      openUploadS3Dialog,
      openDeleteDialog,
    ],
  );

  useEffect(() => {
    setMenuModel(campaignItems);
  }, [campaignItems]);

  return (
    <div>
      <StyledPrimeReactMenuBar model={menuModel} />
      {s3UploadDialog && (
        <CampaignUploadS3Dialog
          onHide={closeUploadS3Dialog}
          visible={s3UploadDialog}
          title={t('upload_s3')}
        />
      )}
      {deleteCampaignDialog && (
        <ConfirmDialog
          backText={generalT('cancel')}
          buttonText={t('remove')}
          isOpen={deleteCampaignDialog}
          title={t('confirm_dialog_user_delete_title')}
          message={t('confirm_dialog_user_delete_message')}
          onClose={onCloseRemoveDialog}
        />
      )}
      {campaignManagementDialog && (
        <CampaignCreateDialog
          onHide={closeManagementDialog}
          visible={campaignManagementDialog}
          title={
            campaignActionMode === ActionMode.CREATE
              ? t('create_campaign')
              : t('edit_campaign')
          }
          campaign={campaignsStore.selectedCampaign}
          mode={campaignActionMode}
        />
      )}
    </div>
  );
};

export default observer(Menubar);
